import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AppRole, SessionContext } from '@ircf/contracts';
import { authApi } from '../lib/api/endpoints.js';
import { ApiError, setUnauthorisedHandler } from '../lib/api/client.js';
import { queryKeys } from '../lib/api/queryKeys.js';

/**
 * Session state.
 *
 * Authentication is owned entirely by the Tame platform and carried in an
 * HTTP-only cookie, so the browser cannot read it. The only way to know who is
 * signed in is to ask the API, which means the session is modelled as a query
 * rather than as client state. That has a useful consequence: signing out in
 * one tab invalidates every other tab on its next fetch.
 */

interface SessionValue {
  /** Null once resolved and unauthenticated; undefined while still resolving. */
  session: SessionContext | null | undefined;
  /** True only during the very first resolution, so guards can hold rendering. */
  isResolving: boolean;
  isAuthenticated: boolean;
  role: AppRole | null;
  /** True when the signed-in user holds any of the supplied roles. */
  hasRole: (...roles: readonly AppRole[]) => boolean;
  /** True when onboarding must be completed before the portal is usable. */
  requiresOnboarding: boolean;
  /** True until the trainer accepts a self-registered client. */
  requiresApproval: boolean;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const SessionCtx = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }): JSX.Element {
  const queryClient = useQueryClient();

  const { data, isPending, refetch } = useQuery({
    queryKey: queryKeys.session(),
    queryFn: async () => {
      try {
        return await authApi.session();
      } catch (error) {
        // A 401 here is the expected answer for an anonymous visitor, not a
        // failure - resolve to null so public routes render immediately.
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    staleTime: 60_000,
    retry: (failureCount, error) =>
      !(error instanceof ApiError && error.status === 401) && failureCount < 2,
  });

  const logoutMutation = useMutation({
    mutationFn: authApi.logout,
    onSettled: () => {
      // Clear everything: cached portal data belongs to the user who just left.
      queryClient.clear();
      queryClient.setQueryData(queryKeys.session(), null);
    },
  });

  // The API client cannot import React state, so it calls back into the app
  // when any request is rejected as unauthenticated.
  useEffect(() => {
    setUnauthorisedHandler(() => {
      queryClient.clear();
      queryClient.setQueryData(queryKeys.session(), null);
    });
    return () => setUnauthorisedHandler(null);
  }, [queryClient]);

  const refresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const logout = useCallback(async () => {
    await logoutMutation.mutateAsync();
  }, [logoutMutation]);

  const value = useMemo<SessionValue>(() => {
    const session = isPending ? undefined : (data ?? null);
    const role = session?.appRole ?? null;

    return {
      session,
      isResolving: isPending,
      isAuthenticated: Boolean(session),
      role,
      hasRole: (...required) => (role ? required.includes(role) : false),
      // Only clients have onboarding; staff accounts are provisioned directly.
      requiresOnboarding: Boolean(
        session?.client &&
          session.client.accountApproved &&
          (!session.client.onboardingComplete || !session.client.consentsComplete),
      ),
      requiresApproval: Boolean(session?.client && !session.client.accountApproved),
      refresh,
      logout,
    };
  }, [data, isPending, refresh, logout]);

  return <SessionCtx.Provider value={value}>{children}</SessionCtx.Provider>;
}

export function useSession(): SessionValue {
  const context = useContext(SessionCtx);
  if (!context) {
    throw new Error('useSession must be used within <SessionProvider>');
  }
  return context;
}

/**
 * The signed-in user, asserted non-null. Only valid beneath a role guard, which
 * has already established that a session exists.
 */
export function useAuthenticatedSession(): SessionContext {
  const { session } = useSession();
  if (!session) {
    throw new Error(
      'useAuthenticatedSession called outside an authenticated route. Wrap the route in <RequireAuth>.',
    );
  }
  return session;
}
